import Color from "color";

import type { DefaultTheme } from "simplydo/react/styled";

import { type UiColor, colorMap } from "../../theme/constants";
import {
  adaptiveContrastTarget,
  emphasizeColor,
  toContrastColor,
} from "../../theme/utils";
import type { UiColorPalette } from "./UIContext";

export type UiPalettes = Record<UiColor, UiColorPalette>;

/** Independent emphasize steps for solid hover / active. */
const STRONG_CONTRAST = 1.25;
const STRONGER_CONTRAST = 1.5;
/** Floor for `onSolid` (on-fill text); extremes ramp higher via adaptive target. */
const ON_SOLID_BASE = 4.5;
/** Floor for `ink` vs white (colour-as-text / border); adaptive for extremes. */
const INK_CONTRAST = 3.1;

function paletteFromFill(fill: string): UiColorPalette {
  /** Ink is the contrast color of the fill against white. Also used to calculate the softer colors. */
  let ink = toContrastColor(fill, {
    targetContrast: adaptiveContrastTarget(fill, {
      base: INK_CONTRAST,
      max: 16,
      power: 12,
    }),
    backdrop: "#ffffff",
  })
    .alpha(0.8)
    .toString();

  const softer = Color(ink).alpha(0.07).string();
  const soft = Color(ink).alpha(0.25).string();

  if (Color(ink).contrast(Color(softer)) < 1.5) {
    ink = Color(ink).darken(0.3).toString();
  }

  return {
    softer,
    soft,
    solid: fill,
    strong: emphasizeColor(fill, { targetContrast: STRONG_CONTRAST }).string(),
    stronger: emphasizeColor(fill, {
      targetContrast: STRONGER_CONTRAST,
    }).string(),
    onSolid: toContrastColor(fill, {
      targetContrast: adaptiveContrastTarget(fill, { base: ON_SOLID_BASE }),
    }).string(),
    ink: ink,
  };
}

export type BuildUiPalettesInput = {
  colorMap: typeof colorMap;
  primaryColour: string;
  secondaryColour: string;
};

/** Precompute every known UI colour palette (call once when building the theme). */
export function buildUiPalettes(input: BuildUiPalettesInput): UiPalettes {
  const palettes = {} as UiPalettes;

  for (const key of Object.keys(input.colorMap) as (keyof typeof colorMap)[]) {
    palettes[key] = paletteFromFill(input.colorMap[key]);

    if (key === "grey") {
      palettes[key].ink = "#4a4a4a";
    }
  }

  palettes.primary = paletteFromFill(input.primaryColour);
  const onPrimaryColor = Color(palettes.primary.onSolid);
  palettes.primary.onSolid = onPrimaryColor.isDark()
    ? onPrimaryColor.toString()
    : onPrimaryColor.lighten(0.5).desaturate(0.5).toString();

  palettes.secondary = paletteFromFill(input.secondaryColour);
  const onSecondaryColor = Color(palettes.secondary.onSolid);
  palettes.secondary.onSolid = onSecondaryColor.isDark()
    ? onSecondaryColor.toString()
    : onSecondaryColor.lighten(0.5).desaturate(0.5).toString();

  // Chrome for use on primary/secondary fills — derived from each fill's onSolid.
  palettes.onPrimary = paletteFromFill(palettes.primary.onSolid);
  palettes.onPrimary.ink = palettes.onPrimary.solid;
  palettes.onSecondary = paletteFromFill(palettes.secondary.onSolid);
  palettes.onSecondary.ink = palettes.onSecondary.solid;

  return palettes;
}

/** Look up a precomputed palette from the theme. */
export function resolveUiPalette(
  theme: DefaultTheme,
  color: UiColor,
): UiColorPalette {
  const palette = theme.colors?.[color];
  if (!palette) {
    throw new Error(`Unknown UI color: ${String(color)}`);
  }
  return palette;
}
